import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import ArrowLeft from '@mui/icons-material/ArrowBackIosNew';
import ArrowRight from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CircularProgress from '@mui/material/CircularProgress';
import * as d3 from 'd3';
import { queryData } from "../../utils/api";

const colors = {
  blues: ['#eff3ff', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c', '#053e78'],
  noValue: '#dcdcdc'
};

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const margin = { top: 20, left: 200, right: 10, bottom: 20 };
const cellHeight = 20;
const cellPad = 1;
const panAmount = 100;

function getColorForValue(count, maxCount, deviceType, timeWindow) {
  if (count === 0) return colors.noValue;
  
  const hourlyMax = deviceType === 'Sap Flow Sensor' ? 9 : 38;
  const maxExpected = timeWindow === 'daily' ? hourlyMax * 24 : hourlyMax;
  const ratio = count / maxExpected;
  const index = Math.min(6, Math.floor(ratio * 7));
  return colors.blues[index];
}

let clipIdCounter = 0;

function drawTimeline(container, data, width, onCellClick, timeWindow, deviceTypes, locationName, allLabels, timezone, deviceIdMap, onLabelClick, startTime, endTime) {
  d3.select(container).selectAll('*').remove();
  
  const clipId = 'status-chart-clip-' + (++clipIdCounter) + '-' + Date.now();
  
  const yLabels = allLabels || Object.keys(data);
  if (yLabels.length === 0) return;
  
  const chartData = [];
  
  const cellUnit = timeWindow === 'daily' ? DAY : HOUR;
  const bucketStart = Math.floor(startTime / cellUnit) * cellUnit;
  const bucketEnd = Math.ceil(endTime / cellUnit) * cellUnit;
  
  yLabels.forEach(label => {
    const deviceData = data[label] || [];
    const bucketMap = {};
    
    let deviceId = '';
    if (deviceData.length > 0 && deviceData[0].meta) {
      deviceId = deviceData[0].meta.deviceId || '';
    }
    
    deviceData.forEach(record => {
      const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
      const timestamp = new Date(timestampStr).getTime();
      const bucketTime = Math.floor(timestamp / cellUnit) * cellUnit;
      bucketMap[bucketTime] = record;
      if (!deviceId && record.meta) {
        deviceId = record.meta.deviceId || '';
      }
    });
    
    for (let t = bucketStart; t <= bucketEnd; t += cellUnit) {
      if (bucketMap[t]) {
        chartData.push({ ...bucketMap[t], row: label });
      } else {
        chartData.push({
          timestamp: new Date(t).toISOString(),
          end: new Date(t + cellUnit).toISOString(),
          value: 0,
          meta: { deviceId: deviceId },
          row: label
        });
      }
    }
  });
  
  if (chartData.length === 0) return;
  
  const height = yLabels.length * cellHeight;
  const svgHeight = height + margin.top + margin.bottom;
  
  const xDomain = [new Date(bucketStart), new Date(bucketEnd)];
  
  const x = d3.scaleTime()
    .domain(xDomain)
    .range([0, width]);
  
  const y = d3.scaleBand()
    .domain(yLabels)
    .range([0, height])
    .padding(0);
  
  const svg = d3.select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', svgHeight)
    .attr('cursor', 'grab')
    .style('display', 'block')
    .style('overflow', 'visible');
  
  const axisFormat = (date) => {
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone
    };
    return date.toLocaleString(undefined, options);
  };
  
  const xAxis = d3.axisTop(x).tickFormat(axisFormat);
  const gX = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`)
    .call(xAxis);
  
  svg.append('defs')
    .append('clipPath')
    .attr('id', clipId)
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height);
  
  const cells = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`)
    .attr('clip-path', `url(#${clipId})`)
    .attr('width', width)
    .style('cursor', 'pointer');
  
  const computeWidth = (d, scale) => {
    const startStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
    const start = new Date(startStr);
    const end = d.end ? new Date(d.end.endsWith('Z') ? d.end : d.end + 'Z') : new Date(start.getTime() + HOUR);
    const w = scale(end) - scale(start);
    return w > cellPad ? w - cellPad : cellPad;
  };
  
  const tooltipClass = 'status-timeline-tooltip-' + Date.now();
  const tooltip = d3.select('body')
    .append('div')
    .attr('class', tooltipClass)
    .style('position', 'absolute')
    .style('display', 'none')
    .style('background', '#000')
    .style('color', '#fff')
    .style('padding', '8px 12px')
    .style('border-radius', '4px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('z-index', 1000)
    .style('line-height', '1.4');
  
  cells.selectAll()
    .data(chartData)
    .enter()
    .append('rect')
    .attr('class', 'cell')
    .attr('x', (d) => {
      const timestampStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
      return x(new Date(timestampStr));
    })
    .attr('y', (d) => y(d.row) + cellPad)
    .attr('width', (d) => computeWidth(d, x))
    .attr('height', y.bandwidth() - 2)
    .attr('fill', (d) => {
      const deviceType = deviceTypes[d.row];
      return getColorForValue(d.value, null, deviceType, timeWindow);
    })
    .on('mouseenter', function(evt, d) {
      d3.select(this)
        .attr('opacity', 0.7)
        .attr('stroke', '#444')
        .attr('stroke-width', 1);
      
      const timestampStr = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
      const date = new Date(timestampStr);
      const options = {
        weekday: 'short', month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: timezone
      };
      const dateStr = date.toLocaleString(undefined, options);
      
      tooltip
        .style('display', 'block')
        .html(`<strong>${d.row.replace(/\s*\[[^\]]+\]$/, '')}</strong><br/>${dateStr} (${timeWindow})<br/>Count: ${d.value}`);
    })
    .on('mousemove', function(evt) {
      tooltip
        .style('left', (evt.pageX + 15) + 'px')
        .style('top', (evt.pageY - 10) + 'px');
    })
    .on('mouseleave', function() {
      d3.select(this)
        .attr('opacity', 1)
        .attr('stroke', 'none');
      tooltip.style('display', 'none');
    })
    .on('click', function(evt, d) {
      if (d.value > 0 && onCellClick) {
        onCellClick(d, locationName);
      }
    });
  
  // Helper to strip device ID suffix from variable labels for display
  const displayLabel = (label) => {
    // Remove trailing " [DEVICE_ID]" from variable sub-row labels
    return label.replace(/\s*\[[^\]]+\]$/, '');
  };

  // Y-axis labels
  const labels = svg.append('g')
    .attr('transform', `translate(0, ${margin.top})`);
  
  labels.selectAll('text')
    .data(yLabels)
    .enter()
    .append('text')
    .attr('x', margin.left - 8)
    .attr('y', (d) => y(d) + y.bandwidth() / 2)
    .attr('text-anchor', 'end')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '11px')
    .attr('fill', '#1e40af')
    .attr('cursor', 'pointer')
    .text(d => {
      const clean = displayLabel(d);
      return clean.length > 28 ? clean.substring(0, 25) + '...' : clean;
    })
    .on('click', function(evt, d) {
      const deviceId = deviceIdMap[d];
      if (deviceId && onLabelClick) {
        onLabelClick(evt, deviceId, locationName);
      }
    });
  
  // Zoom & pan
  const zoom = d3.zoom()
    .scaleExtent([0.5, 50])
    .translateExtent([[0, 0], [width, height]])
    .extent([[0, 0], [width, height]])
    .on('zoom', (event) => {
      const newX = event.transform.rescaleX(x);
      gX.call(xAxis.scale(newX));
      
      cells.selectAll('.cell')
        .attr('x', (d) => {
          const ts = d.timestamp.endsWith('Z') ? d.timestamp : d.timestamp + 'Z';
          return newX(new Date(ts));
        })
        .attr('width', (d) => computeWidth(d, newX));
    });
  
  svg.call(zoom);
  
  // Button controls
  const parentEl = container.closest('.status-timeline-container');
  if (parentEl) {
    const panLeft = parentEl.querySelector('.pan-left');
    const panRight = parentEl.querySelector('.pan-right');
    const zoomIn = parentEl.querySelector('.zoom-in');
    const zoomOut = parentEl.querySelector('.zoom-out');
    const resetBtn = parentEl.querySelector('.reset');
    
    if (panLeft) panLeft.onclick = () => {
      const transform = d3.zoomTransform(svg.node());
      const newTransform = transform.translate(panAmount, 0);
      zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), newTransform);
    };
    if (panRight) panRight.onclick = () => {
      const transform = d3.zoomTransform(svg.node());
      const newTransform = transform.translate(-panAmount, 0);
      zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), newTransform);
    };
    if (zoomIn) zoomIn.onclick = () => {
      const transform = d3.zoomTransform(svg.node());
      const newTransform = transform.scale(1.5);
      zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), newTransform);
    };
    if (zoomOut) zoomOut.onclick = () => {
      const transform = d3.zoomTransform(svg.node());
      const newTransform = transform.scale(1 / 1.5);
      zoom.transform(svg.transition().duration(200).ease(d3.easeLinear), newTransform);
    };
    if (resetBtn) resetBtn.onclick = () => {
      svg.transition().duration(500).ease(d3.easeLinear).call(zoom.transform, d3.zoomIdentity);
    };
  }
  
  return () => {
    d3.select('.' + tooltipClass).remove();
  };
}

export default function StatusTimeline({ location, dateRange, showVariables }) {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  
  const [timeWindow, setTimeWindow] = useState('hourly');
  const [containerWidth, setContainerWidth] = useState(800);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState({});
  const [allLabels, setAllLabels] = useState([]);
  const [deviceIdMap, setDeviceIdMap] = useState({});
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');
  
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  const handleCellClick = (data, locationName) => {
    const cellStart = new Date(data.timestamp);
    let cellEnd;
    if (data.end) {
      cellEnd = new Date(data.end);
    } else {
      cellEnd = new Date(cellStart.getTime() + (timeWindow === 'daily' ? DAY : HOUR));
    }
    
    navigate(`/query-browser?location=${locationName}&device_id=${data.meta.deviceId}&start=${cellStart.toISOString()}&end=${cellEnd.toISOString()}&timezone=${timezone}`);
  };
  
  const handleLabelClick = (evt, deviceId, locationName) => {
    const [startTime, endTime] = dateRange;
    const params = new URLSearchParams({
      location: locationName,
      device_id: deviceId,
      start: startTime.toISOString(),
      end: endTime.toISOString(),
      page: '0'
    });
    
    const url = `/query-browser?${params.toString()}`;
    
    if (evt.button === 1 || evt.ctrlKey || evt.metaKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };
  
  // Auto-detect appropriate time window based on date range
  useEffect(() => {
    if (!dateRange) return;
    const [start, end] = dateRange;
    const durationDays = (end.getTime() - start.getTime()) / DAY;
    if (durationDays > 7) {
      setTimeWindow('daily');
    } else {
      setTimeWindow('hourly');
    }
  }, [dateRange]);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    let timeoutId;
    const resizeObserver = new ResizeObserver(entries => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width);
        }
      }, 100);
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);
  
  useEffect(() => {
    if (!location || !dateRange) return;
    
    setLoading(true);
    setTimelineData(null);
    
    const [startTime, endTime] = dateRange;
    const startISO = startTime.toISOString();
    const endISO = endTime.toISOString();
    
    const idMap = {};
    location.devices.forEach(device => {
      idMap[device.name] = device.serialNumber;
    });
    setDeviceIdMap(idMap);
    
    const promises = location.devices.map(device => {
      return queryData({
        start: startISO,
        end: endISO,
        timezone: "UTC",
        filter: {
          location: location.name,
          device_id: device.serialNumber
        }
      }).then(records => ({
        deviceName: device.name,
        deviceId: device.serialNumber,
        deviceType: device.type,
        records: records
      }));
    });
    
    Promise.all(promises)
      .then(results => {
        const cellUnit = timeWindow === 'daily' ? DAY : HOUR;
        const data = {};
        const types = {};
        const labels = [];
        
        results.forEach(({ deviceName, deviceId, deviceType, records }) => {
          types[deviceName] = deviceType;
          labels.push(deviceName);
          
          // Device-level aggregation
          const buckets = {};
          records.forEach(record => {
            const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
            const timestamp = new Date(timestampStr).getTime();
            const bucketTime = Math.floor(timestamp / cellUnit) * cellUnit;
            
            if (!buckets[bucketTime]) {
              buckets[bucketTime] = {
                timestamp: new Date(bucketTime).toISOString(),
                end: new Date(bucketTime + cellUnit).toISOString(),
                value: 0,
                meta: { deviceId }
              };
            }
            buckets[bucketTime].value += 1;
          });
          
          data[deviceName] = Object.values(buckets).sort((a, b) =>
            new Date(a.timestamp) - new Date(b.timestamp)
          );
          
          // Per-variable aggregation if showVariables is enabled
          if (showVariables) {
            const variableMap = {};
            records.forEach(record => {
              if (!variableMap[record.name]) {
                variableMap[record.name] = {};
              }
              
              const timestampStr = record.timestamp.endsWith('Z') ? record.timestamp : record.timestamp + 'Z';
              const timestamp = new Date(timestampStr).getTime();
              const bucketTime = Math.floor(timestamp / cellUnit) * cellUnit;
              
              if (!variableMap[record.name][bucketTime]) {
                variableMap[record.name][bucketTime] = {
                  timestamp: new Date(bucketTime).toISOString(),
                  end: new Date(bucketTime + cellUnit).toISOString(),
                  value: 0,
                  meta: { deviceId }
                };
              }
              variableMap[record.name][bucketTime].value += 1;
            });
            
            // Sort variable names and add them as sub-rows
            const varNames = Object.keys(variableMap).sort();
            varNames.forEach(varName => {
              // Use unique key that includes device ID to avoid collisions across devices
              const varLabel = `  └ ${varName} [${deviceId}]`;
              labels.push(varLabel);
              types[varLabel] = deviceType;
              
              // Also add to idMap so label clicks work
              idMap[varLabel] = deviceId;
              
              data[varLabel] = Object.values(variableMap[varName]).sort((a, b) =>
                new Date(a.timestamp) - new Date(b.timestamp)
              );
            });
          }
        });
        
        setAllLabels(labels);
        setDeviceIdMap({...idMap});
        setTimelineData(data);
        setDeviceTypes(types);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch timeline data:", err);
        setLoading(false);
      });
  }, [location, dateRange, timeWindow, showVariables]);
  
  useEffect(() => {
    if (!chartRef.current) return;
    
    d3.select(chartRef.current).selectAll('*').remove();
    
    if (!timelineData || !dateRange) return;
    
    const [startTime, endTime] = dateRange;
    const width = containerWidth - margin.left - margin.right;
    const cleanup = drawTimeline(
      chartRef.current,
      timelineData,
      width,
      handleCellClick,
      timeWindow,
      deviceTypes,
      location.name,
      allLabels,
      timezone,
      deviceIdMap,
      handleLabelClick,
      startTime.getTime(),
      endTime.getTime()
    );
    
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineData, containerWidth, timeWindow, deviceTypes, allLabels, timezone, deviceIdMap]);
  
  if (!location) return null;
  
  const durationDays = dateRange 
    ? ((dateRange[1].getTime() - dateRange[0].getTime()) / DAY).toFixed(1)
    : '?';
  
  return (
    <div className="mt-4 status-timeline-container">
      <div className="border rounded-md bg-white" style={{ overflow: 'hidden' }}>
        <div className="px-4 py-2 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-md font-semibold text-purple-800">
              Data Timeline ({durationDays} days)
            </h2>
            <div className="flex items-center gap-3">
              <ToggleButtonGroup
                value={timeWindow}
                exclusive
                onChange={(e, val) => val && setTimeWindow(val)}
                size="small"
                aria-label="change time windows"
                style={{ height: '29px' }}
              >
                <ToggleButton value="hourly" aria-label="hourly" style={{ height: '29px' }}>
                  hourly
                </ToggleButton>
                <ToggleButton value="daily" aria-label="daily" style={{ height: '29px' }}>
                  daily
                </ToggleButton>
              </ToggleButtonGroup>
              
              <div className="flex items-center gap-1 border rounded" style={{ height: '29px' }}>
                <IconButton size="small" className="reset" title="reset zoom/panning" style={{ height: '27px', width: '27px' }}>
                  <HomeIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="pan-left" title="pan left" style={{ height: '27px', width: '27px' }}>
                  <ArrowLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" className="zoom-in" title="zoom in" style={{ height: '27px', width: '27px' }}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="zoom-out" title="zoom out" style={{ height: '27px', width: '27px' }}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="pan-right" title="pan right" style={{ height: '27px', width: '27px' }}>
                  <ArrowRight fontSize="small" />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-4 pt-4 pb-0" ref={containerRef} style={{ minHeight: '120px', position: 'relative' }}>
          {loading && (
            <div 
              className="flex items-center justify-center py-8"
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'white', zIndex: 10
              }}
            >
              <CircularProgress size={24} />
              <span className="ml-3 text-gray-500">Loading timeline...</span>
            </div>
          )}
          
          <div ref={chartRef} className="w-full" style={{ minHeight: 0, lineHeight: 0 }}></div>
        </div>
      </div>
    </div>
  );
}